import { BaseConfig } from '@eika-infrastruktur/config-provider';

import { actionTypes, StringKeyValue } from '../actions/types';
import loadConfig from './config';

type AppActions = import('../actions/types').AppActions;

export interface ContextReducer extends BaseConfig {
    defaultAppType: string;
    appType: string;
    appErrors: string[];
    config: {
        bankregnr: string | null;
        eikaView: boolean;
        data: {
            loggedInReturnUrl: string;
            purchaseAppPath: string;
            text: StringKeyValue;
        };
    };
    localConfigData: {
        text: StringKeyValue;
    };
    isValidCustomer: boolean;
    isValidNafCustomer: boolean;
    showDialog: boolean;
    showReceipt: boolean;
    showApplication: boolean;
    showSigning: boolean;
    showLBSBankSelect?: boolean;
}

export const initialState: ContextReducer = {
    defaultAppType: 'eikabank',
    appType: '',
    appErrors: [],
    config: {
        bankregnr: null,
        eikaView: false,
        data: {
            loggedInReturnUrl: '',
            purchaseAppPath: '',
            text: {},
        },
    },
    localConfigData: {
        text: {},
    },
    isValidCustomer: false,
    isValidNafCustomer: false,
    showDialog: true,
    showReceipt: false,
    showApplication: false,
    showSigning: false,
};

export const contextReducer = (state = initialState, action: AppActions = {} as AppActions): ContextReducer => {
    switch (action.type) {
        case actionTypes.LOAD_CONFIG: {
            return loadConfig(state, action);
        }

        case actionTypes.FETCH_CASE_FULFILLED: {
            const { status } = action.payload;
            const isSigned = status === 'SIGNERT';
            const isSignering = status === 'TIL_SIGNERING';
            const isNullStatus = status === 'NULL_STATUS';
            const isCompletion = ['INNVILGET', 'KONTROLL', 'AVSLAATT'].includes(status);
            return {
                ...state,
                showSigning: isSignering,
                showReceipt: isSigned,
                showDialog: !isNullStatus && !isSignering && !isSigned && !isCompletion,
                showApplication: isCompletion || isNullStatus,
            };
        }

        case actionTypes.FETCH_STATUS_FULFILLED: {
            const { response, paramIncrease, paramType } = action.payload;
            const isSigned = response.status === 'SIGNERT';
            const isSignering = response.status === 'TIL_SIGNERING';
            const isNullStatus = response.status === 'NULL_STATUS';
            const isPaagaaende = response.status === 'SOKNAD_PAAGAAENDE';
            const isCompletion = ['INNVILGET', 'KONTROLL', 'AVSLAATT'].includes(response.status);
            const hasValidParams =
                !!paramType && ['gold', 'business'].includes(paramType) && !Number.isNaN(paramIncrease);
            return {
                ...state,
                showApplication: (hasValidParams || isCompletion) && !isSigned && !isPaagaaende,
                showDialog:
                    (isNullStatus || isPaagaaende) && !hasValidParams && !isSigned && !isSignering && !isCompletion,
                showReceipt: isSigned,
                showSigning: isSignering,
            };
        }

        case actionTypes.SEND_ORDER_FULFILLED: {
            const { status } = action.payload;
            if (status === 'TIL_SIGNERING') {
                return {
                    ...state,
                    showApplication: false,
                    showDialog: false,
                    showReceipt: false,
                    showSigning: true,
                };
            }
            return state;
        }

        case actionTypes.SHOW_DIALOG: {
            return {
                ...state,
                showApplication: false,
                showReceipt: false,
                showSigning: false,
                showDialog: true,
            };
        }

        case actionTypes.FETCH_USER_CONTEXT_FULFILLED: {
            const { isValidNafCustomer, appType } = state;
            const { erKunde } = action.payload;
            return {
                ...state,
                isValidCustomer:
                    erKunde ||
                    isValidNafCustomer ||
                    appType === 'eika' ||
                    appType === 'naf' ||
                    appType === 'agricard' ||
                    appType === 'lbs',
            };
        }

        case actionTypes.FETCH_NAF_CACHE_FULFILLED: {
            const { erMedlem } = action.payload;
            return {
                ...state,
                isValidNafCustomer: erMedlem === null ? state.isValidNafCustomer : erMedlem,
            };
        }

        case actionTypes.FETCH_NAF_MEMBER_FULFILLED: {
            const { erMedlem } = action.payload;
            return {
                ...state,
                isValidNafCustomer: erMedlem || false,
            };
        }

        case actionTypes.SHOW_LBS_SELECT: {
            return {
                ...state,
                showLBSBankSelect: action.payload.show,
            };
        }

        default:
            return state;
    }
};
